import { Component, EventEmitter, OnDestroy, OnInit, ViewEncapsulation, Output, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MatOptionModule, MatRippleModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';

import { DropdownFilter, DynamicDatacenterFilter } from '../../../models/datacenter-filters';
import { pageTransitions } from '../../../animations/page-transitions';
import { DatacenterFilterApiService } from '../../../services/datacenter-filter-api.service';
import { DatacenterType } from '../../../models/datacenter-aggrid-view';
import { LoadingSpinnerComponent } from '../../../components/utils/loading-spinner.component';
import { TranslationService } from '../../../services/translation.service';

// Temporary solution. Hopefully.
// Results were taken from:
// http://sapdbpha.unix.appliarmony.net:8032/com/arcelormittal/
// commercial/ofu/wot/or/OrderAggregates.xsodata/Order?$select=transport_mode,transport_mode_description&$format=json
const staticTransportModeDropdownValues = [
	{
		code: '1',
		translation: 'shipment.tmode.bysea'
	},
	{
		code: '2',
		translation: 'shipment.tmode.byrail'
	},
	{
		code: '3',
		translation: 'shipment.tmode.byroad'
	},
	{
		code: '03',
		translation: 'shipment.tmode.bytrain'
	},
	{
		code: '4',
		translation: 'shipment.tmode.byairplane'
	},
	{
		code: '5',
		translation: 'shipment.tmode.byinlandwaterways'
	},
	{
		code: '6',
		translation: 'shipment.tmode.byrail.feve'
	},
	{
		code: '7',
		translation: 'shipment.tmode.byrailroad'
	},
	{
		code: 'U',
		translation: 'shipment.tmode.notspecified'
	}
];

const staticUserDropdownValues = [
	{
		code: 'DSU',
		translation: 'add-suborg.administrator',
		fixedList: true
	},
	{
		code: 'user',
		translation: 'organizationrole.user',
		fixedList: true
	},
	{
		code: 'hlpdk',
		translation: 'organizationrole.hlpdk',
		fixedList: true
	},
	{
		code: 'subscription',
		translation: 'organizationrole.subscription',
		fixedList: true
	}
];

const staticUserOrganizationStatusDropdownValues = [
	{
		code: 'activ',
		translation: 'active',
		fixedList: true
	},
	{
		code: 'pend',
		translation: 'issue.pending',
		fixedList: true
	},
	{
		code: 'userValidationStatus',
		translation: 'wait.user.validation',
		fixedList: true
	},
	{
		code: 'dact',
		translation: 'organization.status.deactivated',
		fixedList: true
	}
];

const staticUserPersonStatusDropdownValues = [
	{
		code: 'Activ',
		translation: 'active',
		fixedList: true
	},
	{
		code: 'dact',
		translation: 'organization.status.deactivated',
		fixedList: true
	},
	{
		code: 'pend',
		translation: 'issue.pending',
		fixedList: true
	}
];

const staticTransportModeDocumentDropdownValues = [
	{
		code: 'CAFER',
		translation: 'tmode.lorryrailway'
	},
	{
		code: 'CAM',
		translation: 'tmode.lorry'
	},
	{
		code: 'FER',
		translation: 'tmode.rail'
	},
	{
		code: 'FLUV',
		translation: 'tmode.inlandwaterway'
	},
	{
		code: 'MARIT',
		translation: 'tmode.bysea'
	},
	{
		code: 'OTHER',
		translation: 'tmode.othertospecify'
	},
	{
		code: 'PLA',
		translation: 'tmode.byair'
	}
];

const staticDocumentTypeDropdownValues = [
	{
		code: 'IN',
		translation: 'commercial-documents.invoice'
	},
	{
		code: 'OA',
		translation: 'order.card.document.order.acknowledgments'
	},
	{
		code: 'DA',
		translation: 'datacenter-filter.dispatch-advice'
	},
	{
		code: 'CE',
		translation: 'order.card.document.certificates'
	},
	{
		code: 'XCARB',
		translation: 'order.card.xcarb',
	}
];

const staticProformaDropdownValues = [
	{
		code: 'true',
		translation: 'global.yes',
		fixedList: true
	},
	{
		code: 'false',
		translation: 'global.no',
		fixedList: true
	}
];

const staticStockAlertDropdownValues = [
	{
		code: '1',
		translation: 'stock-alert.green',
		fixedList: true
	},
	{
		code: '2',
		translation: 'stock-alert.orange',
		fixedList: true
	},
	{
		code: '3',
		translation: 'stock-alert.red',
		fixedList: true
	}
];

const staticOrganizationTypeDropdownValues = [
	{
		code: 'Agcy',
		translation: 'organization-type.agency'
	},
	{
		code: 'Buyer',
		translation: 'organization-type.buyer'
	},
	{
		code: 'LABO',
		translation: 'organization-type.lab'
	},
	{
		code: 'SelAg',
		translation: 'organization-type.selag'
	},
	{
		code: 'Sell',
		translation: 'organization-type.seller'
	},
	{
		code: 'Suser',
		translation: 'organization-type.super-user'
	}
];

const staticOrderTypeDropdownValues = [
	{
		code: 'spot',
		translation: 'filter.spot-order'
	},
	{
		code: 'scheduled',
		translation: 'filter.schedule-business-order'
	}
];

const staticEcallStatusDropdownValues = [
	{
		code: 'O',
		translation: 'ecall-off.open-or-callable',
		fixedList: true
	},
	{
		code: 'C',
		translation: 'ecall-off.closed-or-called',
		fixedList: true
	}
];

const staticDocumentSubtypeDropdownValues = [
	{
		code: '01',
		translation: 'doc-ce-subtype.01'
	},
	{
		code: '02',
		translation: 'doc-ce-subtype.02'
	},
	{
		code: '17',
		translation: 'doc-ce-subtype.17'
	},
	{
		code: '51',
		translation: 'doc-ce-subtype.51'
	},
	{
		code: '52',
		translation: 'doc-ce-subtype.52'
	},
	{
		code: 'CAR',
		translation: 'doc-ce-subtype.car'
	},
	{
		code: 'CCONF',
		translation: 'doc-ce-subtype.cconf'
	},
	{
		code: 'CCPU',
		translation: 'doc-ce-subtype.ccpu'
	},
	{
		code: 'CEM',
		translation: 'doc-ce-subtype.cem'
	},
	{
		code: 'CERAN',
		translation: 'doc-ce-subtype.ceran'
	},
	{
		code: 'CERRE',
		translation: 'doc-ce-subtype.cerre'
	},
	{
		code: 'CERUS',
		translation: 'doc-ce-subtype.cerus'
	},
	{
		code: 'CINSP',
		translation: 'doc-ce-subtype.cinsp'
	},
	{
		code: 'CTL',
		translation: 'doc-ce-subtype.ctl'
	},
	{
		code: 'FCP',
		translation: 'doc-ce-subtype.fcp'
	},
	{
		code: 'OHSAS',
		translation: 'doc-ce-subtype.ohsas'
	},
	{
		code: 'AVOIR',
		translation: 'doc-in-subtype.AVOIR'
	},
	{
		code: 'DEBIT',
		translation: 'doc-in-subtype.DEBIT'
	},
	{
		code: 'FACTU',
		translation: 'doc-in-subtype.FACTU'
	},
	{
		code: 'N',
		translation: 'doc-oa-subtype.N'
	},
	{
		code: 'O',
		translation: 'doc-oa-subtype.O'
	},
	{
		code: 'A',
		translation: 'doc-xcarb-subtype.a',
		fixedList: true
	},
	{
		code: 'B',
		translation: 'doc-xcarb-subtype.b',
		fixedList: true
	},
	{
		code: 'C',
		translation: 'doc-xcarb-subtype.c',
		fixedList: true
	},
	{
		code: 'D',
		translation: 'doc-xcarb-subtype.d',
		fixedList: true
	},
	{
		code: 'E',
		translation: 'doc-xcarb-subtype.e',
		fixedList: true
	},
	{
		code: 'F',
		translation: 'doc-xcarb-subtype.f',
		fixedList: true
	},
	{
		code: 'G',
		translation: 'doc-xcarb-subtype.g',
		fixedList: true
	}
];

const staticCustomerDefectTypeDropdownValues = [
	{
		code: 'Z/CDCUS00A/0600',
		translation: 'defect.Z/CDCUS00A/0600'
	},
	{
		code: 'Z/CDCUS00C/1400',
		translation: 'defect.Z/CDCUS00C/1400'
	},
	{
		code: 'Z/CDCUS00D/0500',
		translation: 'defect.Z/CDCUS00D/0500'
	},
	{
		code: 'Z/CDCUS00E/0400',
		translation: 'defect.Z/CDCUS00E/0400'
	},
	{
		code: 'Z/CDCUS00F/0400',
		translation: 'defect.Z/CDCUS00F/0400'
	},
	{
		code: 'Z/CDCUS00G/0700',
		translation: 'defect.Z/CDCUS00G/0700'
	},
	{
		code: 'Z/CDCUS00H/0600',
		translation: 'defect.Z/CDCUS00H/0600'
	},
	{
		code: 'Z/CDCUS00I/0600',
		translation: 'defect.Z/CDCUS00I/0600'
	},
	{
		code: 'Z/CDCUS00J/0900',
		translation: 'defect.Z/CDCUS00J/0900'
	},
	{
		code: 'Z/CDCUS00M/0300',
		translation: 'defect.Z/CDCUS00M/0300'
	},
	{
		code: 'Z/CDCUS00B/0100',
		translation: 'defect.Z/CDCUS00B/0100'
	},
	{
		code: 'Z/CDCUS00L/0600',
		translation: 'defect.Z/CDCUS00L/0600'
	},
	{
		code: 'Z/CDCUS00K/0300',
		translation: 'defect.Z/CDCUS00K/0300'
	},
	{
		code: 'Z/CDCUS00B/0200',
		translation: 'defect.Z/CDCUS00B/0200'
	},
	{
		code: 'Z/CDCUS00C/0400',
		translation: 'defect.Z/CDCUS00C/0400'
	},
	{
		code: 'Z/CDCUS00L/0100',
		translation: 'defect.Z/CDCUS00L/0100'
	}
];

const staticIssueOriginDropdownValues = [
	{
		code: 'STA',
		translation: 'issue.origin.sta'
	},
	{
		code: 'ACH',
		translation: 'issue.origin.ach'
	}
];

const staticClaimTypeDropdownValues = [
	{
		code: 'ZL',
		translation: 'claim.type.zl'
	},
	{
		code: 'ZR',
		translation: 'claim.type.zr'
	}
];

const staticSABusinessStatusDropDownValues = [
	{
		code: '01',
		translation: 'saBusinessStatus.start.up'

	},
	{
		code: '02',
		translation: 'saBusinessStatus.running'
	},
	{
		code: '03',
		translation: 'saBusinessStatus.running.out'

	},
	{
		code: '04',
		translation: 'saBusinessStatus.closed'

	},
	{
		code: '05',
		translation: 'saBusinessStatus.low.runner'

	},
	{
		code: '06',
		translation: 'saBusinessStatus.high.runner'

	},
	{
		code: '07',
		translation: 'saBusinessStatus.high.risk.internal'

	},
	{
		code: '08',
		translation: 'saBusinessStatus.on.hold'

	},
	{
		code: '09',
		translation: 'saBusinessStatus.high.risk.external'

	},
];

interface ItemValue {
	code?: string | undefined;
	translation?: string;
}

@Component({
	selector: 'copo-dropdown-filter',
	templateUrl: './view-dropdown-filter.component.html',
	styleUrl: './view-dropdown-filter.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	providers: [DatacenterFilterApiService],
	imports: [
		NgIf,
		NgFor,
		MatIconModule,
		MatRippleModule,
		LoadingSpinnerComponent,

		MatFormFieldModule,

		MatSelectModule,

		MatOptionModule,
		MatButtonModule,
	]
})
export class ViewDropdownFilterComponent implements OnInit, OnDestroy, DynamicDatacenterFilter<DropdownFilter> {
	@Input() filter?: DropdownFilter;
	@Output() readonly removeFilter = new EventEmitter<void>();
	@Input() isDefaultFilter?: boolean;
	filteredItems: ItemValue[] = [];
	isLoading = true;
	showDropdown = false;
	datacenterType: DatacenterType;
	subscriptions = new Subscription();

	constructor(
		private readonly filterApiService: DatacenterFilterApiService,
		private readonly route: ActivatedRoute,
		private readonly translateService: TranslateService,
		readonly translationService: TranslationService,
	) {
		this.datacenterType = <DatacenterType> this.route.snapshot.queryParams['type'];
	}

	ngOnInit() {
		if (this.filter?.getValues().length) {
			this.filteredItems = this.filter.getValues();
		}
		if (this.filter && this.filter.filterId === 'transportMode') {
			this.showDropdown = true;
			this.isLoading = false;
			if (this.datacenterType === 'shipment') {
				this.filteredItems = staticTransportModeDropdownValues.map(x => ({ ...x }));
			} else if (this.datacenterType === 'document') {
				this.filteredItems = staticTransportModeDocumentDropdownValues.map(x => ({ ...x }));
			}
		}

		if (this.filter && this.filter.filterId === 'proforma') {
			this.showDropdown = true;
			this.isLoading = false;
			if (this.datacenterType === 'document') {
				this.filteredItems = staticProformaDropdownValues.map(x => ({ ...x }));
			}
		}

		if (this.filter && this.filter.filterId === 'type') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticDocumentTypeDropdownValues.map(x => ({ ...x }));
		}

		if (this.filter && this.filter.filterId === 'subtype') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticDocumentSubtypeDropdownValues.map(x => ({ ...x }));
		}

		if (this.filter && this.filter.filterId === 'customerDefect') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticCustomerDefectTypeDropdownValues.map(x => ({ ...x }));
		}

		if (this.filter && this.filter.filterId === 'origin') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticIssueOriginDropdownValues.map(x => ({ ...x }));
		}

		if (this.filter && this.filter.filterId === 'claimType') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticClaimTypeDropdownValues.map(x => ({ ...x }));
		}

		if (this.filter && this.filter.filterId === 'businessStatus') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticSABusinessStatusDropDownValues.map(x => ({ ...x }));
		}

		if (this.filter && this.filter.filterId === 'supplierOrderType') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticOrderTypeDropdownValues.map(x => ({ ...x }));
		}

		if (this.filter && this.filter.filterId === 'supplierOrderTypeConcat') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticOrderTypeDropdownValues.map(x => ({ ...x }));
		}

		if (this.filter && this.filter.filterId === 'personMembers.role') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticUserDropdownValues.map(x => ({ ...x }));
		}

		if (this.filter && this.filter.filterId === 'status' && this.datacenterType === 'users') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticUserOrganizationStatusDropdownValues.map(x => ({ ...x }));
		}

		if (this.filter && this.filter.filterId === 'personStatus' && this.datacenterType === 'users') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticUserPersonStatusDropdownValues.map(x => ({ ...x }));
		}

		if (this.filter && this.filter.filterId === 'role') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticOrganizationTypeDropdownValues.map(x => ({ ...x }));
		}

		if (this.filter && this.filter.filterId === 'stockAlert') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticStockAlertDropdownValues.map(x => ({ ...x }));
		}
		if (this.filter && this.filter.filterId === 'ecallStatus') {
			this.showDropdown = true;
			this.isLoading = false;
			this.filteredItems = staticEcallStatusDropdownValues.map(x => ({ ...x }));
		}

		if (this.showDropdown) {
			this.translateAndSortValues();
			this.getTranslatedFilterValue();
			return;
		}

		this.subscriptions.add(this.filterApiService.sendRequest(this.filter).subscribe({
			next: value => {
				this.filteredItems = value;
				this.isLoading = false;
				this.translateAndSortValues();
				this.getTranslatedFilterValue();
			},
			error: (error: unknown) => {
				console.error(error);
				this.isLoading = false;
			}
		}));
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	removeValue(index: number) {
		if (!this.filter) {
			return;
		}
		this.filter.getValues().splice(index, 1);
		if (!this.filter.valueSize()) {
			this.removeFilter.emit();
		}
	}

	trackByFunc(index: number) {
		return index;
	}

	getPreparedFilterValues() {
		if (this.filter) {
			return {
				id: this.filter.filterId,
				values: this.filter.getValues()
					.filter(filterValue => !filterValue.translation || !filterValue.code)
			};
		}
		return undefined;
	}

	selectionChanged(
		item: ItemValue,
		selectionResult: string
	) {
		const selection = this.filteredItems.find(fItem => fItem.code === selectionResult);
		if (!selection) {
			return;
		}
		Object.assign(item, selection);
	}

	private getTranslatedFilterValue() {
		if (!this.filter) {
			return;
		}
		for (const filteredItem of this.filteredItems) {
			for (const filterValue of this.filter.getValues()) {
				if (filteredItem.code === filterValue.code) {
					filterValue.translation = filteredItem.translation;
				}
			}
		}
	}

	private translateAndSortValues() {
		if (this.showDropdown) {
			for (const filterItem of this.filteredItems) {
				this.subscriptions.add(
					this.translateService.get(filterItem.translation ?? '')
						.subscribe({
							next: value => {
								filterItem.translation = <string> value;
							}
						})
				);
			}
		}
		this.filteredItems = this.filteredItems.filter(item => item.translation);
		this.filteredItems = this.filteredItems.toSorted(
			(f1, f2) => (f1.translation ?? '').localeCompare(f2.translation ?? '')
		);
		if (!this.filter?.removeNoValueFilter) {
			const noValueDropdown = {
				code: 'novalue',
				translation: this.translationService.getTR('filter.no-value')
			};
			this.filteredItems.unshift(noValueDropdown);
		}
	}
}
